// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/enterprise-admin/organization/OperationalProfileSection.jsx

import React, { useEffect, useState } from "react";
import { Field, Select, TextInput } from "../../../design-system/components";
import GuidedMultiSelect from "./GuidedMultiSelect";
import OrganizationSectionCard from "./OrganizationSectionCard";
import {
  TIMEZONE_OPTIONS,
  isKnownTimezone,
} from "../shared/timezoneOptions";

const COUNTRIES = [
  "United States",
  "Canada",
  "Mexico",
  "United Kingdom",
  "Ireland",
  "Germany",
  "France",
  "Netherlands",
  "Switzerland",
  "Australia",
  "New Zealand",
  "Singapore",
  "Japan",
  "India",
  "Brazil",
];

const REGIONS = [
  "North America",
  "Latin America",
  "EMEA",
  "European Union",
  "United Kingdom",
  "APAC",
  "Middle East",
  "Africa",
];

const SYSTEMS = [
  "AWS",
  "Google Workspace",
  "Okta",
  "GitHub",
  "Cloudflare",
  "Microsoft Azure",
  "Microsoft 365",
  "Salesforce",
  "ServiceNow",
  "Slack",
  "Jira",
];

function fromCsv(value) {
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function fromLines(value) {
  return String(value || "")
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean);
}

export default function OperationalProfileSection({ draft, setField }) {
  const countryIsKnown = COUNTRIES.includes(draft.headquarters_country);
  const timezoneIsKnown = isKnownTimezone(draft.primary_timezone);

  const [customCountryEnabled, setCustomCountryEnabled] = useState(
    Boolean(draft.headquarters_country && !countryIsKnown)
  );
  const [customTimezoneEnabled, setCustomTimezoneEnabled] = useState(
    Boolean(draft.primary_timezone && !timezoneIsKnown)
  );

  useEffect(() => {
    if (draft.headquarters_country && !COUNTRIES.includes(draft.headquarters_country)) {
      setCustomCountryEnabled(true);
    }
  }, [draft.headquarters_country]);

  useEffect(() => {
    if (draft.primary_timezone && !isKnownTimezone(draft.primary_timezone)) {
      setCustomTimezoneEnabled(true);
    }
  }, [draft.primary_timezone]);

  const handleCountryChange = (value) => {
    if (value === "__other__") {
      setCustomCountryEnabled(true);
      setField("headquarters_country", "");
      return;
    }

    setCustomCountryEnabled(false);
    setField("headquarters_country", value);
  };

  const handleTimezoneChange = (value) => {
    if (value === "__other__") {
      setCustomTimezoneEnabled(true);
      setField("primary_timezone", "");
      return;
    }

    setCustomTimezoneEnabled(false);
    setField("primary_timezone", value);
  };

  return (
    <OrganizationSectionCard
      eyebrow="Operational profile"
      title="Geography, systems, and operating context"
      description="Define where the organization operates and the systems that matter most."
    >
      <div className="enterprise-admin-organization-fields">
        <Field label="Headquarters country">
          <Select
            value={
              customCountryEnabled
                ? "__other__"
                : countryIsKnown
                  ? draft.headquarters_country
                  : ""
            }
            onChange={(event) => handleCountryChange(event.target.value)}
          >
            <option value="">Select country</option>
            {COUNTRIES.map((country) => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
            <option value="__other__">Other</option>
          </Select>
        </Field>

        <Field label="Primary timezone">
          <Select
            value={
              customTimezoneEnabled
                ? "__other__"
                : timezoneIsKnown
                  ? draft.primary_timezone
                  : ""
            }
            onChange={(event) => handleTimezoneChange(event.target.value)}
          >
            <option value="">Select timezone</option>
            {TIMEZONE_OPTIONS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
            <option value="__other__">Other IANA timezone</option>
          </Select>
        </Field>

        {customCountryEnabled ? (
          <Field label="Custom headquarters country">
            <TextInput
              value={draft.headquarters_country || ""}
              onChange={(event) =>
                setField("headquarters_country", event.target.value)
              }
              placeholder="Enter country"
              autoFocus
            />
          </Field>
        ) : null}

        {customTimezoneEnabled ? (
          <Field label="Custom IANA timezone">
            <TextInput
              value={draft.primary_timezone || ""}
              onChange={(event) =>
                setField("primary_timezone", event.target.value)
              }
              placeholder="Region/City"
            />
          </Field>
        ) : null}

        <div className="enterprise-admin-organization-field-wide">
          <GuidedMultiSelect
            label="Operating countries"
            value={fromCsv(draft.operating_countries_csv)}
            options={COUNTRIES}
            onChange={(items) =>
              setField("operating_countries_csv", items.join(", "))
            }
            placeholder="Select operating countries"
            helper="Choose every country where the organization actively operates."
          />
        </div>

        <div className="enterprise-admin-organization-field-wide">
          <GuidedMultiSelect
            label="Regions"
            value={fromCsv(draft.regions_csv)}
            options={REGIONS}
            onChange={(items) => setField("regions_csv", items.join(", "))}
            placeholder="Select operating regions"
            helper="Use standardized regions for reporting and regulatory context."
          />
        </div>

        <div className="enterprise-admin-organization-field-wide">
          <GuidedMultiSelect
            label="Critical systems"
            value={fromLines(draft.critical_systems_text)}
            options={SYSTEMS}
            onChange={(items) =>
              setField("critical_systems_text", items.join("\n"))
            }
            placeholder="Select critical systems"
            helper="Choose connected platforms and add any other business-critical systems."
          />
        </div>
      </div>
    </OrganizationSectionCard>
  );
}
